import React, { useState, useMemo, useRef, useEffect } from 'react';

/**
 * CupDrawer – custom tray for the "cup" campaign.
 *
 * Country filter logic:
 * - All country toggles start OFF. "Filters active" switch is hidden.
 * - Toggling a country ON auto-activates "Filters active" and reveals the switch.
 * - "Filters active" OFF: country switches grey out (state preserved), map shows all.
 * - "Filters active" ON: reinstates last country selections, map greys non-matching.
 */

// Fast city-scale distance in feet between two lat/lng pairs
const distanceFeet = (lat1, lng1, lat2, lng2) => {
  const dx = (lng2 - lng1) * Math.cos(lat1 * Math.PI / 180);
  const dy = lat2 - lat1;
  return Math.sqrt(dx * dx + dy * dy) * 364000;
};

// Bounding box for proximity: NW corner to SE corner
const PROXIMITY_BOUNDS = {
  north: 37.813568,
  south: 37.698051,
  west: -122.528088,
  east: -122.343615
};

const isWithinProximityBounds = (userLocation) => {
  if (!userLocation) return false;
  const [lat, lng] = userLocation;
  return (
    lat <= PROXIMITY_BOUNDS.north &&
    lat >= PROXIMITY_BOUNDS.south &&
    lng >= PROXIMITY_BOUNDS.west &&
    lng <= PROXIMITY_BOUNDS.east
  );
};

const formatDistance = (feet) => {
  if (feet < 1000) return `${Math.round(feet)} ft`;
  const miles = feet / 5280;
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
};

const ToggleSwitch = ({ checked, onChange, label, disabled }) => (
  <label
    style={{
      position: 'relative',
      display: 'inline-block',
      width: '40px',
      height: '22px',
      flexShrink: 0,
      opacity: disabled ? 0.4 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
      transition: 'opacity 0.2s'
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <input
      type="checkbox"
      role="switch"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
      aria-label={label}
    />
    <span style={{
      position: 'absolute',
      cursor: 'pointer',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: checked ? '#0d6efd' : '#ccc',
      transition: '0.2s',
      borderRadius: '999px'
    }} />
    <span style={{
      position: 'absolute',
      height: '18px',
      width: '18px',
      left: checked ? '20px' : '2px',
      top: '2px',
      backgroundColor: 'white',
      transition: '0.2s',
      borderRadius: '50%',
      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
    }} />
  </label>
);

const CupDrawer = ({
  points,
  teams,
  showWatchParties,
  onToggleWatchParties,
  showVenues,
  onToggleVenues,
  enabledCountries,
  onToggleCountry,
  filtersActive,
  onToggleFiltersActive,
  filtersEverActivated,
  userLocation,
  filteredPoints,
  onSelectPoint,
  onFilterZoom
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [nearbyIndex, setNearbyIndex] = useState(0);
  const [nearbyActivated, setNearbyActivated] = useState(false);

  const openDrawer = () => {
    setIsOpen(true);
    setIsOpening(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsOpening(false);
      });
    });
  };

  const closeDrawer = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  // Whether user is within the proximity bounding box
  const userInBounds = useMemo(() => isWithinProximityBounds(userLocation), [userLocation]);

  // Sort active (non-greyed) points: by distance if in SF, alphabetical otherwise
  const sortedNearby = useMemo(() => {
    if (!filteredPoints) return [];
    const active = filteredPoints.filter(pt => pt && !pt.greyed && pt.lat && pt.lng);
    if (userInBounds && userLocation) {
      return active
        .map(pt => ({
          ...pt,
          _dist: distanceFeet(userLocation[0], userLocation[1], Number(pt.lat), Number(pt.lng))
        }))
        .sort((a, b) => a._dist - b._dist);
    }
    // Outside SF: alphabetical, no distance
    return [...active].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [userInBounds, userLocation, filteredPoints]);

  // Reset index when the sorted list changes
  const safeIndex = sortedNearby.length > 0 ? Math.min(nearbyIndex, sortedNearby.length - 1) : 0;
  const currentNearby = sortedNearby[safeIndex] || null;

  // When filters change (sortedNearby identity changes), hide navigator and zoom to extent
  const prevSortedRef = useRef(sortedNearby);
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      prevSortedRef.current = sortedNearby;
      return;
    }
    if (prevSortedRef.current !== sortedNearby) {
      setNearbyActivated(false);
      setNearbyIndex(0);
      if (onFilterZoom && sortedNearby.length > 0) {
        onFilterZoom(sortedNearby);
      }
    }
    prevSortedRef.current = sortedNearby;
  }, [sortedNearby, onFilterZoom]);

  // Build country list with counts from venue data
  const countriesWithCounts = useMemo(() => {
    const countMap = new Map();

    const venues = (points || []).filter(pt => pt && pt.type === 'Bar');
    for (const venue of venues) {
      const affinities = venue.national_affinities || [];
      for (const countryId of affinities) {
        countMap.set(countryId, (countMap.get(countryId) || 0) + 1);
      }
    }

    const teamsArr = teams || [];
    const result = [];
    for (const [countryId, count] of countMap.entries()) {
      const team = teamsArr.find(t => t.id === countryId);
      if (team) {
        result.push({
          id: countryId,
          name: team.name,
          flag_url: team.flag_url,
          count
        });
      } else {
        result.push({
          id: countryId,
          name: countryId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          flag_url: null,
          count
        });
      }
    }

    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [points, teams]);

  return (
    <>
      {/* Bottom tray bar */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ffffff',
          borderTop: '1px solid #dee2e6',
          padding: '10px 16px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {/* Nearest venue navigator */}
        {userInBounds && nearbyActivated && currentNearby && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            maxWidth: '400px',
            gap: '4px'
          }}>
            {/* Previous button */}
            <button
              type="button"
              disabled={safeIndex === 0}
              onClick={() => {
                const newIdx = Math.max(0, nearbyIndex - 1);
                setNearbyIndex(newIdx);
                if (onSelectPoint && sortedNearby[newIdx]) onSelectPoint(sortedNearby[newIdx]);
              }}
              style={{
                background: safeIndex === 0 ? '#ccc' : '#0d6efd',
                border: 'none',
                fontSize: '20px',
                fontWeight: 700,
                cursor: safeIndex === 0 ? 'default' : 'pointer',
                padding: '6px 12px',
                borderRadius: '6px',
                color: '#ffffff',
                flexShrink: 0,
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: safeIndex === 0 ? 0.5 : 1
              }}
              aria-label="Previous venue"
            >
              ‹
            </button>

            {/* Venue info */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
              textAlign: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <span style={{ flexShrink: 0, fontSize: '16px' }}>
                  {currentNearby['icon-emoji'] || '📍'}
                </span>
                <span style={{
                  fontWeight: 600,
                  fontSize: '15px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {currentNearby.title}
                </span>
              </div>
              {userInBounds && currentNearby._dist != null && (
                <span style={{ color: '#495057', fontSize: '12px', marginTop: '2px' }}>
                  ~{formatDistance(currentNearby._dist)}
                </span>
              )}
            </div>

            {/* Next button */}
            {safeIndex < sortedNearby.length - 1 ? (
              <button
                type="button"
                onClick={() => {
                  const newIdx = Math.min(sortedNearby.length - 1, nearbyIndex + 1);
                  setNearbyIndex(newIdx);
                  if (onSelectPoint && sortedNearby[newIdx]) onSelectPoint(sortedNearby[newIdx]);
                }}
                style={{
                  background: '#0d6efd',
                  border: 'none',
                  fontSize: '20px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  color: '#ffffff',
                  flexShrink: 0,
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Next venue"
              >
                ›
              </button>
            ) : (
              <span style={{ width: '38px', flexShrink: 0 }} />
            )}
          </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <button
            type="button"
            onClick={openDrawer}
            style={{
              fontSize: '14px',
              fontWeight: 600,
              padding: '8px 20px',
              borderRadius: '6px',
              background: '#ffffff',
              border: '2px solid #0d6efd',
              color: '#0d6efd',
              cursor: 'pointer'
            }}
            aria-label="Legend and Settings"
          >
            Filters ⚽ 📺
          </button>
          {userInBounds && sortedNearby.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setNearbyActivated(true);
                setNearbyIndex(0);
                if (onSelectPoint && sortedNearby[0]) onSelectPoint(sortedNearby[0]);
              }}
              style={{
                fontSize: '14px',
                fontWeight: 600,
                padding: '8px 20px',
                borderRadius: '6px',
                background: '#0d6efd',
                border: 'none',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              Nearest place to watch
            </button>
          )}
        </div>
      </div>

      {/* Modal overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center'
          }}
        >
          {/* Backdrop */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              opacity: (isClosing || isOpening) ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out'
            }}
            onClick={closeDrawer}
          />

          {/* Panel */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '85vh',
              backgroundColor: '#ffffff',
              borderRadius: '12px 12px 0 0',
              padding: '20px',
              overflowY: 'auto',
              boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
              transform: (isClosing || isOpening) ? 'translateY(100%)' : 'translateY(0)',
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <h5 style={{ margin: 0, fontWeight: 700 }}>
                Filters
              </h5>
              <button
                type="button"
                onClick={closeDrawer}
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '6px 14px',
                  borderRadius: '6px',
                  background: '#ffffff',
                  border: '2px solid #0d6efd',
                  color: '#0d6efd',
                  cursor: 'pointer'
                }}
                aria-label="Close"
              >
                Close
              </button>
            </div>

            {/* Watch Parties toggle */}
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              padding: '14px 16px',
              marginBottom: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>
                  📺 Watch Parties
                </span>
                <ToggleSwitch
                  checked={showWatchParties}
                  onChange={onToggleWatchParties}
                  label="Toggle Watch Parties"
                />
              </div>
              <p style={{
                margin: '8px 0 0 0',
                fontSize: '13px',
                color: '#6c757d',
                lineHeight: 1.5
              }}>
                Watch parties have large screens, live atmosphere, and fan energy.
              </p>
            </div>

            {/* Venues toggle + country filters */}
            <div style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              padding: '14px 16px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>
                  ⚽ Venues
                </span>
                <ToggleSwitch
                  checked={showVenues}
                  onChange={onToggleVenues}
                  label="Toggle Venues"
                />
              </div>

              {/* Country filters (indented inside venues) */}
              {countriesWithCounts.length > 0 && (
                <div style={{
                  marginTop: '14px',
                  paddingTop: '12px',
                  borderTop: '1px solid #dee2e6',
                  paddingLeft: '12px',
                  opacity: showVenues ? 1 : 0.45,
                  transition: 'opacity 0.2s'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '10px'
                  }}>
                    Show venues supporting
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {countriesWithCounts.map((country) => {
                      const isOn = enabledCountries.has(country.id);
                      return (
                        <div
                          key={country.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px',
                            color: '#212529'
                          }}>
                            {country.flag_url && (
                              <img
                                src={country.flag_url}
                                alt={`${country.name} flag`}
                                style={{
                                  width: '20px',
                                  height: 'auto',
                                  borderRadius: '2px'
                                }}
                              />
                            )}
                            <span>{country.name} ({country.count})</span>
                          </span>
                          <ToggleSwitch
                            checked={isOn}
                            onChange={() => onToggleCountry(country.id)}
                            label={`Toggle ${country.name}`}
                            disabled={!showVenues || (filtersEverActivated && !filtersActive)}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Filters active switch — only visible after first country toggle */}
                  {filtersEverActivated && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: '12px',
                      paddingTop: '10px',
                      borderTop: '1px solid #dee2e6'
                    }}>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: filtersActive ? '#0d6efd' : '#6c757d'
                      }}>
                        Filters active
                      </span>
                      <ToggleSwitch
                        checked={filtersActive}
                        onChange={onToggleFiltersActive}
                        label="Toggle country filters active"
                        disabled={!showVenues}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CupDrawer;
